import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, CheckCircle2, ChevronDown, Loader2, MessageCircle } from "lucide-react";
import { SERVICE_OPTIONS, WHATSAPP_URL } from "@/lib/site";
import { Reveal, SectionLabel, GlowButton } from "@/components/primitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", business_name: "", email: "", phone: "", service: "", details: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [serverError, setServerError] = useState("");
  const [selectOpen, setSelectOpen] = useState(false);
  const selectRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (selectRef.current && !selectRef.current.contains(e.target)) setSelectOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your name.";
    if (!form.business_name.trim()) e.business_name = "Please enter your business name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email address.";
    if (form.phone.replace(/\D/g, "").length < 7) e.phone = "Enter a valid phone number.";
    if (!form.service) e.service = "Select a service.";
    if (form.details.trim().length < 5) e.details = "Tell us a little about your project.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setServerError("");
    if (!validate()) return;
    setStatus("submitting");
    try {
      await axios.post(`${API}/leads`, form);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setServerError(
        err?.response?.data?.detail?.[0]?.msg ||
          "Something went wrong sending your request. Please try again or reach us on WhatsApp.",
      );
    }
  };

  const reset = () => {
    setForm(EMPTY);
    setErrors({});
    setStatus("idle");
  };

  return (
    <section id="contact" className="relative overflow-hidden py-20 sm:py-28 lg:py-32" data-testid="contact-section">
      {/* colorful soft background */}
      <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-br from-[#EEF2FF] via-white to-[#FFF1EB]" />
      <div className="pointer-events-none absolute -left-20 top-1/4 -z-10 h-64 w-64 rounded-full bg-[#7C3AED]/10 blur-3xl" />
      <div className="pointer-events-none absolute -right-16 bottom-10 -z-10 h-64 w-64 rounded-full bg-[#FF6B35]/10 blur-3xl" />

      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14">
          <Reveal>
            <SectionLabel color="#FF6B35">Contact</SectionLabel>
            <h2 className="mt-4 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-[#111827] sm:text-5xl lg:text-6xl">
              Have something
              <br />
              <span className="text-gradient-warm">worth building?</span>
            </h2>
            <p className="mt-5 max-w-md text-lg text-slate-600">
              Tell us what you want to build, automate or advertise. We&apos;ll get back to you with
              a clear next step.
            </p>

            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-3 rounded-2xl bg-white px-5 py-4 shadow-card ring-1 ring-slate-100 transition-transform hover:-translate-y-0.5"
              data-testid="contact-whatsapp-link"
            >
              <span className="grid h-10 w-10 place-items-center rounded-full bg-emerald-500 text-white">
                <MessageCircle className="h-5 w-5" />
              </span>
              <span>
                <span className="block text-sm font-bold text-[#111827]">Prefer WhatsApp?</span>
                <span className="block text-xs font-medium text-emerald-600">+91 8178194087</span>
              </span>
            </a>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="relative rounded-3xl bg-white p-6 shadow-float ring-1 ring-slate-100 sm:p-8">
              <AnimatePresence mode="wait">
                {status === "success" ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex min-h-[440px] flex-col items-center justify-center text-center"
                    data-testid="contact-success"
                  >
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 200, damping: 12 }}
                      className="grid h-16 w-16 place-items-center rounded-full bg-emerald-50 text-emerald-500"
                    >
                      <CheckCircle2 className="h-8 w-8" />
                    </motion.span>
                    <h3 className="mt-6 font-display text-2xl font-bold text-[#111827]">Request sent!</h3>
                    <p className="mt-2 max-w-sm text-sm text-slate-600">
                      Thanks for reaching out to Digital Advyzer. We&apos;ve received your project
                      request and will get back to you shortly.
                    </p>
                    <GlowButton variant="secondary" onClick={reset} className="mt-7" data-testid="contact-reset">
                      Send another request
                    </GlowButton>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onSubmit={onSubmit}
                    noValidate
                    className="space-y-4"
                    data-testid="contact-form"
                  >
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Field label="Name" error={errors.name}>
                        <input type="text" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls(errors.name)} placeholder="Your name" data-testid="contact-input-name" />
                      </Field>
                      <Field label="Business Name" error={errors.business_name}>
                        <input type="text" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} className={inputCls(errors.business_name)} placeholder="Company / brand" data-testid="contact-input-business" />
                      </Field>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Field label="Email" error={errors.email}>
                        <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls(errors.email)} placeholder="you@company.com" data-testid="contact-input-email" />
                      </Field>
                      <Field label="Phone" error={errors.phone}>
                        <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls(errors.phone)} placeholder="+91 …" data-testid="contact-input-phone" />
                      </Field>
                    </div>

                    <Field label="Service" error={errors.service}>
                      <div className="relative" ref={selectRef}>
                        <button
                          type="button"
                          onClick={() => setSelectOpen((v) => !v)}
                          className={`${inputCls(errors.service)} flex items-center justify-between text-left`}
                          aria-haspopup="listbox"
                          aria-expanded={selectOpen}
                          data-testid="contact-select-service"
                        >
                          <span className={form.service ? "text-[#111827]" : "text-slate-400"}>{form.service || "Select a service"}</span>
                          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${selectOpen ? "rotate-180" : ""}`} />
                        </button>
                        <AnimatePresence>
                          {selectOpen && (
                            <motion.ul
                              initial={{ opacity: 0, y: -6 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -6 }}
                              transition={{ duration: 0.2 }}
                              className="absolute z-20 mt-2 w-full overflow-hidden rounded-xl bg-white p-1 shadow-float ring-1 ring-slate-100"
                              role="listbox"
                            >
                              {SERVICE_OPTIONS.map((opt) => (
                                <li key={opt}>
                                  <button
                                    type="button"
                                    onClick={() => { set("service", opt); setSelectOpen(false); }}
                                    className={`w-full rounded-lg px-3 py-2.5 text-left text-sm transition-colors hover:bg-slate-50 ${form.service === opt ? "font-semibold text-[#2563EB]" : "text-slate-700"}`}
                                    data-testid={`contact-service-option-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                                  >
                                    {opt}
                                  </button>
                                </li>
                              ))}
                            </motion.ul>
                          )}
                        </AnimatePresence>
                      </div>
                    </Field>

                    <Field label="Project Details" error={errors.details}>
                      <textarea rows={4} value={form.details} onChange={(e) => set("details", e.target.value)} className={`${inputCls(errors.details)} resize-none`} placeholder="What are you trying to build, automate or advertise?" data-testid="contact-input-details" />
                    </Field>

                    {serverError && <p className="text-sm text-rose-500" data-testid="contact-server-error">{serverError}</p>}

                    <GlowButton type="submit" disabled={status === "submitting"} className="w-full" data-testid="contact-submit-button">
                      {status === "submitting" ? (
                        <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
                      ) : (
                        <>Start the Conversation <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></>
                      )}
                    </GlowButton>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

const inputCls = (err) =>
  `w-full rounded-xl border bg-white px-4 py-3 text-sm text-[#111827] placeholder:text-slate-400 transition-colors focus:outline-none focus:ring-2 ${
    err ? "border-rose-300 focus:ring-rose-200" : "border-slate-200 focus:border-[#2563EB] focus:ring-[#2563EB]/20"
  }`;

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</span>
      {children}
      {error && <span className="mt-1 block text-xs text-rose-500">{error}</span>}
    </label>
  );
}
